// Menus, toasts and the "⋯" overflow menus (DESIGN-SPEC §6.9, §6.12; PL-21, PL-31).
//
// Namespace `overlays`; owner: the shared overlay components (ui/components/ui/menu.tsx,
// toast.tsx) and the surfaces that fold their secondary actions into ⋯.
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// `{name}` is the row's title or a person; `{email}` an invited address; `{count}` a number.
// Terms follow the app catalogs ("More actions for …", "connections").
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  moreActions: 'More actions',
  moreActionsFor: 'More actions for {name}',
  notifications: 'Notifications',
  dismiss: 'Dismiss',
  undo: 'Undo',
  tryAgain: 'Try again',
  runNow: 'Run now',
  runNowDetail: 'Runs the published version once.',
  runFailedNoResult: 'Run failed · No Result was saved.',
  publishFirst: 'Publish a version first.',
  setUpConnections: 'Set up connections',
  invitationRevoked: 'Invitation for {email} revoked.',
  waitingCount: 'Waiting: {count}',
  passagesInSearch: 'Passages in search: {count}',
  runFailed: 'Couldn’t run {name}.',
  switchOffFailed: 'Couldn’t switch off {name}.',
  archiveFailed: 'Couldn’t archive {name}.',
  archiveTitle: 'Archive agent?',
  archiveDescription:
    '{name} stops running, and runs waiting to start are cancelled. Past runs and their chats are kept.',
  archiveConfirm: 'Archive {name}',
  deleteMemoryTitle: 'Delete memory?',
  deleteMemoryDescription: 'Poneva will stop using “{name}” in your chats.',
  sourceEditReason: 'Ask the owner of this source to change it.',
  agentStatusOff: 'Off',
  agentStatusOn: 'On',
  agentStatusOnDemand: 'On demand',
  agentStatusNeedsYou: 'Needs you',
  agentTurnOn: 'Turn on',
  agentSwitchedOn: 'Switched on.',
  agentStartsWhenOn: '{schedule} · starts when on',
  agentTurnOnQuestion: 'Runs {schedule} ({place}). Turn on?',
  agentTurnOnCustom: 'Runs on a custom schedule ({place}). Turn on?',
  scheduleHourly: 'Every hour',
  scheduleDaily: 'Every day at {time}',
  scheduleWeekdays: 'Weekdays at {time}',
  scheduleWeekly: '{weekday}s at {time}',
  scheduleCustom: 'Custom schedule',
  scheduleNone: 'Not scheduled',
  searchAgents: 'Search agents',
};

export type OverlaysCopyKey = keyof typeof en;
type Copy = Record<OverlaysCopyKey, string>;

const bg: Copy = {
  moreActions: 'Още действия',
  moreActionsFor: 'Още действия за {name}',
  notifications: 'Известия',
  dismiss: 'Затвори',
  undo: 'Отмени',
  tryAgain: 'Опитай отново',
  runNow: 'Стартирай сега',
  runNowDetail: 'Стартира публикуваната версия веднъж.',
  runFailedNoResult: 'Изпълнението е неуспешно · Не беше запазен резултат.',
  publishFirst: 'Първо публикувайте версия.',
  setUpConnections: 'Настрой връзките',
  invitationRevoked: 'Поканата за {email} е оттеглена.',
  waitingCount: 'Чакащи: {count}',
  passagesInSearch: 'Пасажи в търсенето: {count}',
  runFailed: 'Неуспешно стартиране на {name}.',
  switchOffFailed: 'Неуспешно изключване на {name}.',
  archiveFailed: 'Неуспешно архивиране на {name}.',
  archiveTitle: 'Да се архивира ли агентът?',
  archiveDescription:
    '{name} спира да работи, а изпълненията, които чакат да започнат, се отменят. Предишните изпълнения и техните чатове се запазват.',
  archiveConfirm: 'Архивирай {name}',
  deleteMemoryTitle: 'Да се изтрие ли споменът?',
  deleteMemoryDescription: 'Poneva вече няма да използва „{name}“ във вашите чатове.',
  sourceEditReason: 'Помолете собственика на този източник да го промени.',
  agentStatusOff: 'Изключен',
  agentStatusOn: 'Включен',
  agentStatusOnDemand: 'При поискване',
  agentStatusNeedsYou: 'Чака ви',
  agentTurnOn: 'Включи',
  agentSwitchedOn: 'Включено.',
  agentStartsWhenOn: '{schedule} · стартира, когато е включен',
  agentTurnOnQuestion: 'Изпълнява се {schedule} ({place}). Да се включи ли?',
  agentTurnOnCustom: 'Изпълнява се по персонализиран график ({place}). Да се включи ли?',
  scheduleHourly: 'Всеки час',
  scheduleDaily: 'Всеки ден в {time}',
  scheduleWeekdays: 'В делнични дни в {time}',
  scheduleWeekly: 'Всяка седмица, {weekday}, в {time}',
  scheduleCustom: 'Персонализиран график',
  scheduleNone: 'Без график',
  searchAgents: 'Търсене на агенти',
};

const cs: Copy = {
  moreActions: 'Další akce',
  moreActionsFor: 'Další akce pro {name}',
  notifications: 'Oznámení',
  dismiss: 'Zavřít',
  undo: 'Vrátit zpět',
  tryAgain: 'Zkusit znovu',
  runNow: 'Spustit hned',
  runNowDetail: 'Jednou spustí zveřejněnou verzi.',
  runFailedNoResult: 'Spuštění selhalo · Nebyl uložen žádný výsledek.',
  publishFirst: 'Nejprve zveřejněte verzi.',
  setUpConnections: 'Nastavit připojení',
  invitationRevoked: 'Pozvánka pro {email} byla zrušena.',
  waitingCount: 'Čeká: {count}',
  passagesInSearch: 'Pasáže ve vyhledávání: {count}',
  runFailed: '{name} se nepodařilo spustit.',
  switchOffFailed: '{name} se nepodařilo vypnout.',
  archiveFailed: '{name} se nepodařilo archivovat.',
  archiveTitle: 'Archivovat agenta?',
  archiveDescription:
    '{name} přestane běžet a spuštění, která čekají na start, se zruší. Předchozí spuštění a jejich chaty zůstanou zachovány.',
  archiveConfirm: 'Archivovat {name}',
  deleteMemoryTitle: 'Smazat vzpomínku?',
  deleteMemoryDescription: 'Poneva přestane ve vašich chatech používat „{name}“.',
  sourceEditReason: 'Požádejte vlastníka tohoto zdroje o změnu.',
  agentStatusOff: 'Vypnuto',
  agentStatusOn: 'Zapnuto',
  agentStatusOnDemand: 'Na vyžádání',
  agentStatusNeedsYou: 'Čeká na vás',
  agentTurnOn: 'Zapnout',
  agentSwitchedOn: 'Zapnuto.',
  agentStartsWhenOn: '{schedule} · spustí se po zapnutí',
  agentTurnOnQuestion: 'Spouští se {schedule} ({place}). Zapnout?',
  agentTurnOnCustom: 'Spouští se podle vlastního plánu ({place}). Zapnout?',
  scheduleHourly: 'Každou hodinu',
  scheduleDaily: 'Každý den v {time}',
  scheduleWeekdays: 'Ve všední dny v {time}',
  scheduleWeekly: 'Týdně, {weekday} v {time}',
  scheduleCustom: 'Vlastní plán',
  scheduleNone: 'Bez plánu',
  searchAgents: 'Hledat agenty',
};

const da: Copy = {
  moreActions: 'Flere handlinger',
  moreActionsFor: 'Flere handlinger til {name}',
  notifications: 'Notifikationer',
  dismiss: 'Luk',
  undo: 'Fortryd',
  tryAgain: 'Prøv igen',
  runNow: 'Kør nu',
  runNowDetail: 'Kører den udgivne version én gang.',
  runFailedNoResult: 'Kørslen mislykkedes · Der blev ikke gemt noget resultat.',
  publishFirst: 'Udgiv en version først.',
  setUpConnections: 'Opsæt forbindelser',
  invitationRevoked: 'Invitationen til {email} er trukket tilbage.',
  waitingCount: 'Venter: {count}',
  passagesInSearch: 'Passager i søgningen: {count}',
  runFailed: '{name} kunne ikke køres.',
  switchOffFailed: '{name} kunne ikke slås fra.',
  archiveFailed: '{name} kunne ikke arkiveres.',
  archiveTitle: 'Arkivér agenten?',
  archiveDescription:
    '{name} holder op med at køre, og kørsler, der venter på at starte, annulleres. Tidligere kørsler og deres chats bevares.',
  archiveConfirm: 'Arkivér {name}',
  deleteMemoryTitle: 'Slet minde?',
  deleteMemoryDescription: 'Poneva holder op med at bruge »{name}« i dine chats.',
  sourceEditReason: 'Bed ejeren af denne kilde om at ændre det.',
  agentStatusOff: 'Fra',
  agentStatusOn: 'Til',
  agentStatusOnDemand: 'Efter behov',
  agentStatusNeedsYou: 'Venter på dig',
  agentTurnOn: 'Slå til',
  agentSwitchedOn: 'Slået til.',
  agentStartsWhenOn: '{schedule} · starter, når den er slået til',
  agentTurnOnQuestion: 'Kører {schedule} ({place}). Slå til?',
  agentTurnOnCustom: 'Kører efter en tilpasset plan ({place}). Slå til?',
  scheduleHourly: 'Hver time',
  scheduleDaily: 'Hver dag kl. {time}',
  scheduleWeekdays: 'Hverdage kl. {time}',
  scheduleWeekly: 'Hver {weekday} kl. {time}',
  scheduleCustom: 'Tilpasset plan',
  scheduleNone: 'Ingen plan',
  searchAgents: 'Søg i agenter',
};

const de: Copy = {
  moreActions: 'Weitere Aktionen',
  moreActionsFor: 'Weitere Aktionen für {name}',
  notifications: 'Benachrichtigungen',
  dismiss: 'Schließen',
  undo: 'Rückgängig',
  tryAgain: 'Erneut versuchen',
  runNow: 'Jetzt ausführen',
  runNowDetail: 'Führt die veröffentlichte Version einmal aus.',
  runFailedNoResult: 'Ausführung fehlgeschlagen · Es wurde kein Ergebnis gespeichert.',
  publishFirst: 'Veröffentlichen Sie zuerst eine Version.',
  setUpConnections: 'Verbindungen einrichten',
  invitationRevoked: 'Einladung für {email} widerrufen.',
  waitingCount: 'Ausstehend: {count}',
  passagesInSearch: 'Abschnitte in der Suche: {count}',
  runFailed: '{name} konnte nicht ausgeführt werden.',
  switchOffFailed: '{name} konnte nicht ausgeschaltet werden.',
  archiveFailed: '{name} konnte nicht archiviert werden.',
  archiveTitle: 'Agent archivieren?',
  archiveDescription:
    '{name} wird nicht mehr ausgeführt, und wartende Ausführungen werden abgebrochen. Frühere Ausführungen und ihre Chats bleiben erhalten.',
  archiveConfirm: '{name} archivieren',
  deleteMemoryTitle: 'Erinnerung löschen?',
  deleteMemoryDescription: 'Poneva verwendet „{name}“ nicht mehr in Ihren Chats.',
  sourceEditReason: 'Bitten Sie den Eigentümer dieser Quelle, sie zu ändern.',
  agentStatusOff: 'Aus',
  agentStatusOn: 'An',
  agentStatusOnDemand: 'Bei Bedarf',
  agentStatusNeedsYou: 'Wartet auf Sie',
  agentTurnOn: 'Einschalten',
  agentSwitchedOn: 'Eingeschaltet.',
  agentStartsWhenOn: '{schedule} · startet nach dem Einschalten',
  agentTurnOnQuestion: 'Läuft {schedule} ({place}). Einschalten?',
  agentTurnOnCustom: 'Läuft nach einem eigenen Zeitplan ({place}). Einschalten?',
  scheduleHourly: 'Jede Stunde',
  scheduleDaily: 'Jeden Tag um {time}',
  scheduleWeekdays: 'Werktags um {time}',
  scheduleWeekly: 'Jeden {weekday} um {time}',
  scheduleCustom: 'Eigener Zeitplan',
  scheduleNone: 'Kein Zeitplan',
  searchAgents: 'Agenten suchen',
};

const el: Copy = {
  moreActions: 'Περισσότερες ενέργειες',
  moreActionsFor: 'Περισσότερες ενέργειες για {name}',
  notifications: 'Ειδοποιήσεις',
  dismiss: 'Κλείσιμο',
  undo: 'Αναίρεση',
  tryAgain: 'Δοκιμάστε ξανά',
  runNow: 'Εκτέλεση τώρα',
  runNowDetail: 'Εκτελεί μία φορά τη δημοσιευμένη έκδοση.',
  runFailedNoResult: 'Η εκτέλεση απέτυχε · Δεν αποθηκεύτηκε αποτέλεσμα.',
  publishFirst: 'Δημοσιεύστε πρώτα μια έκδοση.',
  setUpConnections: 'Ρύθμιση συνδέσεων',
  invitationRevoked: 'Η πρόσκληση για {email} ανακλήθηκε.',
  waitingCount: 'Σε αναμονή: {count}',
  passagesInSearch: 'Αποσπάσματα στην αναζήτηση: {count}',
  runFailed: 'Δεν ήταν δυνατή η εκτέλεση του {name}.',
  switchOffFailed: 'Δεν ήταν δυνατή η απενεργοποίηση του {name}.',
  archiveFailed: 'Δεν ήταν δυνατή η αρχειοθέτηση του {name}.',
  archiveTitle: 'Αρχειοθέτηση του πράκτορα;',
  archiveDescription:
    'Το {name} σταματά να εκτελείται και οι εκτελέσεις που περιμένουν να ξεκινήσουν ακυρώνονται. Οι προηγούμενες εκτελέσεις και οι συνομιλίες τους διατηρούνται.',
  archiveConfirm: 'Αρχειοθέτηση {name}',
  deleteMemoryTitle: 'Διαγραφή ανάμνησης;',
  deleteMemoryDescription: 'Το Poneva δεν θα χρησιμοποιεί πλέον το «{name}» στις συνομιλίες σας.',
  sourceEditReason: 'Ζητήστε από τον κάτοχο αυτής της πηγής να το αλλάξει.',
  agentStatusOff: 'Ανενεργός',
  agentStatusOn: 'Ενεργός',
  agentStatusOnDemand: 'Κατ’ απαίτηση',
  agentStatusNeedsYou: 'Σας περιμένει',
  agentTurnOn: 'Ενεργοποίηση',
  agentSwitchedOn: 'Ενεργοποιήθηκε.',
  agentStartsWhenOn: '{schedule} · ξεκινά όταν ενεργοποιηθεί',
  agentTurnOnQuestion: 'Εκτελείται {schedule} ({place}). Ενεργοποίηση;',
  agentTurnOnCustom: 'Εκτελείται με προσαρμοσμένο πρόγραμμα ({place}). Ενεργοποίηση;',
  scheduleHourly: 'Κάθε ώρα',
  scheduleDaily: 'Κάθε μέρα στις {time}',
  scheduleWeekdays: 'Τις καθημερινές στις {time}',
  scheduleWeekly: 'Κάθε {weekday} στις {time}',
  scheduleCustom: 'Προσαρμοσμένο πρόγραμμα',
  scheduleNone: 'Χωρίς πρόγραμμα',
  searchAgents: 'Αναζήτηση agents',
};

const es: Copy = {
  moreActions: 'Otras acciones',
  moreActionsFor: 'Otras acciones para {name}',
  notifications: 'Notificaciones',
  dismiss: 'Cerrar',
  undo: 'Deshacer',
  tryAgain: 'Volver a intentarlo',
  runNow: 'Ejecutar ahora',
  runNowDetail: 'Ejecuta una vez la versión publicada.',
  runFailedNoResult: 'La ejecución falló · No se guardó ningún resultado.',
  publishFirst: 'Publica primero una versión.',
  setUpConnections: 'Configurar conexiones',
  invitationRevoked: 'Invitación para {email} revocada.',
  waitingCount: 'Pendientes: {count}',
  passagesInSearch: 'Fragmentos en la búsqueda: {count}',
  runFailed: 'No se pudo ejecutar {name}.',
  switchOffFailed: 'No se pudo desactivar {name}.',
  archiveFailed: 'No se pudo archivar {name}.',
  archiveTitle: '¿Archivar el agente?',
  archiveDescription:
    '{name} deja de ejecutarse y se cancelan las ejecuciones que esperan para empezar. Las ejecuciones anteriores y sus chats se conservan.',
  archiveConfirm: 'Archivar {name}',
  deleteMemoryTitle: '¿Eliminar el recuerdo?',
  deleteMemoryDescription: 'Poneva dejará de usar «{name}» en tus chats.',
  sourceEditReason: 'Pide a la persona propietaria de esta fuente que lo cambie.',
  agentStatusOff: 'Desactivado',
  agentStatusOn: 'Activado',
  agentStatusOnDemand: 'Bajo demanda',
  agentStatusNeedsYou: 'Te necesita',
  agentTurnOn: 'Activar',
  agentSwitchedOn: 'Activado.',
  agentStartsWhenOn: '{schedule} · empieza al activarlo',
  agentTurnOnQuestion: 'Se ejecuta {schedule} ({place}). ¿Activar?',
  agentTurnOnCustom: 'Se ejecuta con una programación personalizada ({place}). ¿Activar?',
  scheduleHourly: 'Cada hora',
  scheduleDaily: 'Todos los días a las {time}',
  scheduleWeekdays: 'Entre semana a las {time}',
  scheduleWeekly: 'Cada {weekday} a las {time}',
  scheduleCustom: 'Programación personalizada',
  scheduleNone: 'Sin programar',
  searchAgents: 'Buscar agentes',
};

const et: Copy = {
  moreActions: 'Rohkem toiminguid',
  moreActionsFor: 'Rohkem toiminguid: {name}',
  notifications: 'Teavitused',
  dismiss: 'Sulge',
  undo: 'Võta tagasi',
  tryAgain: 'Proovi uuesti',
  runNow: 'Käivita kohe',
  runNowDetail: 'Käivitab avaldatud versiooni üks kord.',
  runFailedNoResult: 'Käivitamine ebaõnnestus · Tulemust ei salvestatud.',
  publishFirst: 'Avalda kõigepealt versioon.',
  setUpConnections: 'Seadista ühendused',
  invitationRevoked: 'Kutse aadressile {email} on tühistatud.',
  waitingCount: 'Ootel: {count}',
  passagesInSearch: 'Lõike otsingus: {count}',
  runFailed: '{name} käivitamine ebaõnnestus.',
  switchOffFailed: '{name} väljalülitamine ebaõnnestus.',
  archiveFailed: '{name} arhiveerimine ebaõnnestus.',
  archiveTitle: 'Kas arhiveerida agent?',
  archiveDescription:
    '{name} lõpetab töötamise ja käivitumist ootavad käivitused tühistatakse. Varasemad käivitused ja nende vestlused säilivad.',
  archiveConfirm: 'Arhiveeri {name}',
  deleteMemoryTitle: 'Kas kustutada mälestus?',
  deleteMemoryDescription: 'Poneva ei kasuta enam „{name}“ sinu vestlustes.',
  sourceEditReason: 'Palu selle allika omanikul seda muuta.',
  agentStatusOff: 'Väljas',
  agentStatusOn: 'Sees',
  agentStatusOnDemand: 'Nõudmisel',
  agentStatusNeedsYou: 'Ootab sind',
  agentTurnOn: 'Lülita sisse',
  agentSwitchedOn: 'Sisse lülitatud.',
  agentStartsWhenOn: '{schedule} · käivitub sisselülitamisel',
  agentTurnOnQuestion: 'Käivitub {schedule} ({place}). Lülitada sisse?',
  agentTurnOnCustom: 'Käivitub kohandatud ajakava järgi ({place}). Lülitada sisse?',
  scheduleHourly: 'Iga tund',
  scheduleDaily: 'Iga päev kell {time}',
  scheduleWeekdays: 'Tööpäeviti kell {time}',
  scheduleWeekly: 'Iga nädal, {weekday}, kell {time}',
  scheduleCustom: 'Kohandatud ajakava',
  scheduleNone: 'Ajastamata',
  searchAgents: 'Otsi agente',
};

const fi: Copy = {
  moreActions: 'Lisää toimintoja',
  moreActionsFor: 'Lisää toimintoja: {name}',
  notifications: 'Ilmoitukset',
  dismiss: 'Sulje',
  undo: 'Kumoa',
  tryAgain: 'Yritä uudelleen',
  runNow: 'Suorita nyt',
  runNowDetail: 'Suorittaa julkaistun version kerran.',
  runFailedNoResult: 'Suoritus epäonnistui · Tulosta ei tallennettu.',
  publishFirst: 'Julkaise ensin versio.',
  setUpConnections: 'Määritä yhteydet',
  invitationRevoked: 'Kutsu osoitteeseen {email} peruttiin.',
  waitingCount: 'Odottaa: {count}',
  passagesInSearch: 'Katkelmia haussa: {count}',
  runFailed: 'Kohdetta {name} ei voitu suorittaa.',
  switchOffFailed: 'Kohdetta {name} ei voitu kytkeä pois päältä.',
  archiveFailed: 'Kohdetta {name} ei voitu arkistoida.',
  archiveTitle: 'Arkistoidaanko agentti?',
  archiveDescription:
    '{name} lakkaa toimimasta, ja käynnistymistä odottavat suoritukset perutaan. Aiemmat suoritukset ja niiden keskustelut säilyvät.',
  archiveConfirm: 'Arkistoi {name}',
  deleteMemoryTitle: 'Poistetaanko muisto?',
  deleteMemoryDescription: 'Poneva lakkaa käyttämästä muistoa ”{name}” keskusteluissasi.',
  sourceEditReason: 'Pyydä tämän lähteen omistajaa muuttamaan sitä.',
  agentStatusOff: 'Pois päältä',
  agentStatusOn: 'Päällä',
  agentStatusOnDemand: 'Tarvittaessa',
  agentStatusNeedsYou: 'Odottaa sinua',
  agentTurnOn: 'Kytke päälle',
  agentSwitchedOn: 'Kytketty päälle.',
  agentStartsWhenOn: '{schedule} · käynnistyy, kun se on päällä',
  agentTurnOnQuestion: 'Suoritetaan {schedule} ({place}). Kytketäänkö päälle?',
  agentTurnOnCustom: 'Suoritetaan mukautetun aikataulun mukaan ({place}). Kytketäänkö päälle?',
  scheduleHourly: 'Joka tunti',
  scheduleDaily: 'Joka päivä klo {time}',
  scheduleWeekdays: 'Arkisin klo {time}',
  scheduleWeekly: 'Joka {weekday} klo {time}',
  scheduleCustom: 'Mukautettu aikataulu',
  scheduleNone: 'Ei aikataulua',
  searchAgents: 'Hae agentteja',
};

const fr: Copy = {
  moreActions: 'Plus d’actions',
  moreActionsFor: 'Plus d’actions pour {name}',
  notifications: 'Notifications',
  dismiss: 'Fermer',
  undo: 'Annuler',
  tryAgain: 'Réessayer',
  runNow: 'Exécuter maintenant',
  runNowDetail: 'Exécute une fois la version publiée.',
  runFailedNoResult: 'Échec de l’exécution · Aucun résultat n’a été enregistré.',
  publishFirst: 'Publiez d’abord une version.',
  setUpConnections: 'Configurer les connexions',
  invitationRevoked: 'Invitation pour {email} révoquée.',
  waitingCount: 'En attente : {count}',
  passagesInSearch: 'Passages dans la recherche : {count}',
  runFailed: 'Impossible d’exécuter {name}.',
  switchOffFailed: 'Impossible de désactiver {name}.',
  archiveFailed: 'Impossible d’archiver {name}.',
  archiveTitle: 'Archiver l’agent ?',
  archiveDescription:
    '{name} cesse de s’exécuter et les exécutions en attente de démarrage sont annulées. Les exécutions passées et leurs chats sont conservés.',
  archiveConfirm: 'Archiver {name}',
  deleteMemoryTitle: 'Supprimer le souvenir ?',
  deleteMemoryDescription: 'Poneva cessera d’utiliser « {name} » dans vos chats.',
  sourceEditReason: 'Demandez au propriétaire de cette source de le modifier.',
  agentStatusOff: 'Désactivé',
  agentStatusOn: 'Activé',
  agentStatusOnDemand: 'À la demande',
  agentStatusNeedsYou: 'Vous attend',
  agentTurnOn: 'Activer',
  agentSwitchedOn: 'Activé.',
  agentStartsWhenOn: '{schedule} · démarre une fois activé',
  agentTurnOnQuestion: 'S’exécute {schedule} ({place}). Activer ?',
  agentTurnOnCustom: 'S’exécute selon un planning personnalisé ({place}). Activer ?',
  scheduleHourly: 'Toutes les heures',
  scheduleDaily: 'Tous les jours à {time}',
  scheduleWeekdays: 'En semaine à {time}',
  scheduleWeekly: 'Chaque {weekday} à {time}',
  scheduleCustom: 'Planning personnalisé',
  scheduleNone: 'Non planifié',
  searchAgents: 'Rechercher des agents',
};

const ga: Copy = {
  moreActions: 'Tuilleadh gníomhaíochtaí',
  moreActionsFor: 'Tuilleadh gníomhaíochtaí le haghaidh {name}',
  notifications: 'Fógraí',
  dismiss: 'Dún',
  undo: 'Cealaigh',
  tryAgain: 'Bain triail eile as',
  runNow: 'Rith anois',
  runNowDetail: 'Ritheann sé an leagan foilsithe uair amháin.',
  runFailedNoResult: 'Theip ar an rith · Níor sábháladh aon toradh.',
  publishFirst: 'Foilsigh leagan ar dtús.',
  setUpConnections: 'Socraigh naisc',
  invitationRevoked: 'Cuireadh an cuireadh chuig {email} ar ceal.',
  waitingCount: 'Ag fanacht: {count}',
  passagesInSearch: 'Sleachta sa chuardach: {count}',
  runFailed: 'Níorbh fhéidir {name} a rith.',
  switchOffFailed: 'Níorbh fhéidir {name} a mhúchadh.',
  archiveFailed: 'Níorbh fhéidir {name} a chartlannú.',
  archiveTitle: 'An gníomhaire a chartlannú?',
  archiveDescription:
    'Stopfaidh {name} de bheith ag rith, agus cuirfear ar ceal na rití atá ag fanacht le tosú. Coinneofar na rití roimhe seo agus a gcomhráite.',
  archiveConfirm: 'Cartlannaigh {name}',
  deleteMemoryTitle: 'An chuimhne a scriosadh?',
  deleteMemoryDescription: 'Ní úsáidfidh Poneva “{name}” i do chomhráite a thuilleadh.',
  sourceEditReason: 'Iarr ar úinéir na foinse seo é a athrú.',
  agentStatusOff: 'As',
  agentStatusOn: 'Air',
  agentStatusOnDemand: 'Ar éileamh',
  agentStatusNeedsYou: 'Ag fanacht leat',
  agentTurnOn: 'Cas air',
  agentSwitchedOn: 'Casta air.',
  agentStartsWhenOn: '{schedule} · tosaíonn nuair atá sé air',
  agentTurnOnQuestion: 'Ritheann {schedule} ({place}). Cas air?',
  agentTurnOnCustom: 'Ritheann ar sceideal saincheaptha ({place}). Cas air?',
  scheduleHourly: 'Gach uair an chloig',
  scheduleDaily: 'Gach lá ag {time}',
  scheduleWeekdays: 'Laethanta oibre ag {time}',
  scheduleWeekly: 'Gach {weekday} ag {time}',
  scheduleCustom: 'Sceideal saincheaptha',
  scheduleNone: 'Gan sceideal',
  searchAgents: 'Cuardaigh gníomhairí',
};

const hr: Copy = {
  moreActions: 'Više radnji',
  moreActionsFor: 'Više radnji za {name}',
  notifications: 'Obavijesti',
  dismiss: 'Zatvori',
  undo: 'Poništi',
  tryAgain: 'Pokušaj ponovno',
  runNow: 'Pokreni sada',
  runNowDetail: 'Jednom pokreće objavljenu verziju.',
  runFailedNoResult: 'Pokretanje nije uspjelo · Nijedan rezultat nije spremljen.',
  publishFirst: 'Najprije objavite verziju.',
  setUpConnections: 'Postavi veze',
  invitationRevoked: 'Pozivnica za {email} je opozvana.',
  waitingCount: 'Na čekanju: {count}',
  passagesInSearch: 'Odlomci u pretraživanju: {count}',
  runFailed: '{name} nije moguće pokrenuti.',
  switchOffFailed: '{name} nije moguće isključiti.',
  archiveFailed: '{name} nije moguće arhivirati.',
  archiveTitle: 'Arhivirati agenta?',
  archiveDescription:
    '{name} prestaje s radom, a pokretanja koja čekaju početak otkazuju se. Prethodna pokretanja i njihovi razgovori se čuvaju.',
  archiveConfirm: 'Arhiviraj {name}',
  deleteMemoryTitle: 'Izbrisati uspomenu?',
  deleteMemoryDescription: 'Poneva više neće koristiti „{name}” u vašim razgovorima.',
  sourceEditReason: 'Zamolite vlasnika ovog izvora da to promijeni.',
  agentStatusOff: 'Isključeno',
  agentStatusOn: 'Uključeno',
  agentStatusOnDemand: 'Na zahtjev',
  agentStatusNeedsYou: 'Čeka vas',
  agentTurnOn: 'Uključi',
  agentSwitchedOn: 'Uključeno.',
  agentStartsWhenOn: '{schedule} · pokreće se kad je uključen',
  agentTurnOnQuestion: 'Pokreće se {schedule} ({place}). Uključiti?',
  agentTurnOnCustom: 'Pokreće se prema prilagođenom rasporedu ({place}). Uključiti?',
  scheduleHourly: 'Svaki sat',
  scheduleDaily: 'Svaki dan u {time}',
  scheduleWeekdays: 'Radnim danima u {time}',
  scheduleWeekly: 'Tjedno, {weekday} u {time}',
  scheduleCustom: 'Prilagođeni raspored',
  scheduleNone: 'Bez rasporeda',
  searchAgents: 'Pretraži agente',
};

const hu: Copy = {
  moreActions: 'További műveletek',
  moreActionsFor: 'További műveletek: {name}',
  notifications: 'Értesítések',
  dismiss: 'Bezárás',
  undo: 'Visszavonás',
  tryAgain: 'Újrapróbálás',
  runNow: 'Futtatás most',
  runNowDetail: 'Egyszer lefuttatja a közzétett verziót.',
  runFailedNoResult: 'A futtatás sikertelen · Nem lett mentve eredmény.',
  publishFirst: 'Előbb tegyen közzé egy verziót.',
  setUpConnections: 'Kapcsolatok beállítása',
  invitationRevoked: '{email} meghívója visszavonva.',
  waitingCount: 'Várakozik: {count}',
  passagesInSearch: 'Szakaszok a keresésben: {count}',
  runFailed: 'Nem sikerült futtatni: {name}.',
  switchOffFailed: 'Nem sikerült kikapcsolni: {name}.',
  archiveFailed: 'Nem sikerült archiválni: {name}.',
  archiveTitle: 'Archiválja az ügynököt?',
  archiveDescription:
    'Az ügynök ({name}) leáll, és az indulásra váró futtatások megszakadnak. A korábbi futtatások és csevegéseik megmaradnak.',
  archiveConfirm: '{name} archiválása',
  deleteMemoryTitle: 'Törli az emléket?',
  deleteMemoryDescription:
    'A Poneva a továbbiakban nem használja ezt az emléket a csevegéseiben: „{name}”.',
  sourceEditReason: 'Kérje meg a forrás tulajdonosát, hogy módosítsa.',
  agentStatusOff: 'Kikapcsolva',
  agentStatusOn: 'Bekapcsolva',
  agentStatusOnDemand: 'Igény szerint',
  agentStatusNeedsYou: 'Önre vár',
  agentTurnOn: 'Bekapcsolás',
  agentSwitchedOn: 'Bekapcsolva.',
  agentStartsWhenOn: '{schedule} · bekapcsolás után indul',
  agentTurnOnQuestion: 'Fut: {schedule} ({place}). Bekapcsolja?',
  agentTurnOnCustom: 'Egyéni ütemezés szerint fut ({place}). Bekapcsolja?',
  scheduleHourly: 'Óránként',
  scheduleDaily: 'Minden nap {time}-kor',
  scheduleWeekdays: 'Hétköznap {time}-kor',
  scheduleWeekly: 'Hetente, {weekday} {time}-kor',
  scheduleCustom: 'Egyéni ütemezés',
  scheduleNone: 'Nincs ütemezve',
  searchAgents: 'Ügynökök keresése',
};

const it: Copy = {
  moreActions: 'Più azioni',
  moreActionsFor: 'Più azioni per {name}',
  notifications: 'Notifiche',
  dismiss: 'Chiudi',
  undo: 'Annulla',
  tryAgain: 'Riprova',
  runNow: 'Esegui ora',
  runNowDetail: 'Esegue una volta la versione pubblicata.',
  runFailedNoResult: 'Esecuzione non riuscita · Nessun risultato è stato salvato.',
  publishFirst: 'Pubblica prima una versione.',
  setUpConnections: 'Configura le connessioni',
  invitationRevoked: 'Invito per {email} revocato.',
  waitingCount: 'In attesa: {count}',
  passagesInSearch: 'Passaggi nella ricerca: {count}',
  runFailed: 'Impossibile eseguire {name}.',
  switchOffFailed: 'Impossibile disattivare {name}.',
  archiveFailed: 'Impossibile archiviare {name}.',
  archiveTitle: 'Archiviare l’agente?',
  archiveDescription:
    '{name} smette di essere eseguito e le esecuzioni in attesa di avvio vengono annullate. Le esecuzioni passate e le relative chat vengono conservate.',
  archiveConfirm: 'Archivia {name}',
  deleteMemoryTitle: 'Eliminare il ricordo?',
  deleteMemoryDescription: 'Poneva smetterà di usare «{name}» nelle tue chat.',
  sourceEditReason: 'Chiedi al proprietario di questa fonte di modificarla.',
  agentStatusOff: 'Spento',
  agentStatusOn: 'Acceso',
  agentStatusOnDemand: 'Su richiesta',
  agentStatusNeedsYou: 'Ti aspetta',
  agentTurnOn: 'Accendi',
  agentSwitchedOn: 'Acceso.',
  agentStartsWhenOn: '{schedule} · parte quando è acceso',
  agentTurnOnQuestion: 'Viene eseguito {schedule} ({place}). Accendere?',
  agentTurnOnCustom: 'Viene eseguito con una pianificazione personalizzata ({place}). Accendere?',
  scheduleHourly: 'Ogni ora',
  scheduleDaily: 'Ogni giorno alle {time}',
  scheduleWeekdays: 'Nei giorni feriali alle {time}',
  scheduleWeekly: 'Ogni {weekday} alle {time}',
  scheduleCustom: 'Pianificazione personalizzata',
  scheduleNone: 'Non pianificato',
  searchAgents: 'Cerca agenti',
};

const lt: Copy = {
  moreActions: 'Daugiau veiksmų',
  moreActionsFor: 'Daugiau veiksmų, skirtų {name}',
  notifications: 'Pranešimai',
  dismiss: 'Uždaryti',
  undo: 'Anuliuoti',
  tryAgain: 'Bandyti dar kartą',
  runNow: 'Paleisti dabar',
  runNowDetail: 'Vieną kartą paleidžia paskelbtą versiją.',
  runFailedNoResult: 'Paleidimas nepavyko · Joks rezultatas neišsaugotas.',
  publishFirst: 'Pirmiausia paskelbkite versiją.',
  setUpConnections: 'Nustatyti ryšius',
  invitationRevoked: 'Kvietimas {email} atšauktas.',
  waitingCount: 'Laukia: {count}',
  passagesInSearch: 'Ištraukos paieškoje: {count}',
  runFailed: 'Nepavyko paleisti {name}.',
  switchOffFailed: 'Nepavyko išjungti {name}.',
  archiveFailed: 'Nepavyko archyvuoti {name}.',
  archiveTitle: 'Archyvuoti agentą?',
  archiveDescription:
    '{name} nustos veikti, o paleidimai, laukiantys pradžios, bus atšaukti. Ankstesni paleidimai ir jų pokalbiai išsaugomi.',
  archiveConfirm: 'Archyvuoti {name}',
  deleteMemoryTitle: 'Ištrinti prisiminimą?',
  deleteMemoryDescription: 'Poneva nebenaudos „{name}“ jūsų pokalbiuose.',
  sourceEditReason: 'Paprašykite šio šaltinio savininko tai pakeisti.',
  agentStatusOff: 'Išjungtas',
  agentStatusOn: 'Įjungtas',
  agentStatusOnDemand: 'Pagal poreikį',
  agentStatusNeedsYou: 'Laukia jūsų',
  agentTurnOn: 'Įjungti',
  agentSwitchedOn: 'Įjungta.',
  agentStartsWhenOn: '{schedule} · paleidžiamas įjungus',
  agentTurnOnQuestion: 'Vykdomas {schedule} ({place}). Įjungti?',
  agentTurnOnCustom: 'Vykdomas pagal pasirinktinį tvarkaraštį ({place}). Įjungti?',
  scheduleHourly: 'Kas valandą',
  scheduleDaily: 'Kasdien {time}',
  scheduleWeekdays: 'Darbo dienomis {time}',
  scheduleWeekly: 'Kas savaitę, {weekday}, {time}',
  scheduleCustom: 'Pasirinktinis tvarkaraštis',
  scheduleNone: 'Nesuplanuota',
  searchAgents: 'Ieškoti agentų',
};

const lv: Copy = {
  moreActions: 'Citas darbības',
  moreActionsFor: 'Citas darbības: {name}',
  notifications: 'Paziņojumi',
  dismiss: 'Aizvērt',
  undo: 'Atsaukt',
  tryAgain: 'Mēģināt vēlreiz',
  runNow: 'Palaist tūlīt',
  runNowDetail: 'Vienreiz palaiž publicēto versiju.',
  runFailedNoResult: 'Palaišana neizdevās · Neviens rezultāts netika saglabāts.',
  publishFirst: 'Vispirms publicējiet versiju.',
  setUpConnections: 'Iestatīt savienojumus',
  invitationRevoked: 'Uzaicinājums uz {email} ir atsaukts.',
  waitingCount: 'Gaida: {count}',
  passagesInSearch: 'Fragmenti meklēšanā: {count}',
  runFailed: 'Neizdevās palaist {name}.',
  switchOffFailed: 'Neizdevās izslēgt {name}.',
  archiveFailed: 'Neizdevās arhivēt {name}.',
  archiveTitle: 'Vai arhivēt aģentu?',
  archiveDescription:
    '{name} pārstāj darboties, un palaidieni, kas gaida sākšanu, tiek atcelti. Iepriekšējie palaidieni un to tērzēšanas tiek saglabāti.',
  archiveConfirm: 'Arhivēt {name}',
  deleteMemoryTitle: 'Vai dzēst atmiņu?',
  deleteMemoryDescription: 'Poneva vairs neizmantos “{name}” jūsu tērzēšanās.',
  sourceEditReason: 'Lūdziet šī avota īpašniekam to mainīt.',
  agentStatusOff: 'Izslēgts',
  agentStatusOn: 'Ieslēgts',
  agentStatusOnDemand: 'Pēc pieprasījuma',
  agentStatusNeedsYou: 'Gaida jūs',
  agentTurnOn: 'Ieslēgt',
  agentSwitchedOn: 'Ieslēgts.',
  agentStartsWhenOn: '{schedule} · sāk darboties pēc ieslēgšanas',
  agentTurnOnQuestion: 'Darbojas {schedule} ({place}). Ieslēgt?',
  agentTurnOnCustom: 'Darbojas pēc pielāgota grafika ({place}). Ieslēgt?',
  scheduleHourly: 'Katru stundu',
  scheduleDaily: 'Katru dienu plkst. {time}',
  scheduleWeekdays: 'Darbdienās plkst. {time}',
  scheduleWeekly: 'Katru nedēļu, {weekday}, plkst. {time}',
  scheduleCustom: 'Pielāgots grafiks',
  scheduleNone: 'Nav ieplānots',
  searchAgents: 'Meklēt aģentus',
};

const mt: Copy = {
  moreActions: 'Aktar azzjonijiet',
  moreActionsFor: 'Aktar azzjonijiet għal {name}',
  notifications: 'Notifiki',
  dismiss: 'Agħlaq',
  undo: 'Erġa’ lura',
  tryAgain: 'Erġa’ pprova',
  runNow: 'Ħaddem issa',
  runNowDetail: 'Iħaddem il-verżjoni ppubblikata darba.',
  runFailedNoResult: 'It-tħaddim falla · Ma ġie salvat l-ebda riżultat.',
  publishFirst: 'L-ewwel ippubblika verżjoni.',
  setUpConnections: 'Issettja l-konnessjonijiet',
  invitationRevoked: 'L-istedina għal {email} ġiet irtirata.',
  waitingCount: 'Qed jistennew: {count}',
  passagesInSearch: 'Siltiet fit-tfittxija: {count}',
  runFailed: 'Ma setax jitħaddem {name}.',
  switchOffFailed: 'Ma setax jintefa {name}.',
  archiveFailed: 'Ma setax jiġi arkivjat {name}.',
  archiveTitle: 'Tarkivja l-aġent?',
  archiveDescription:
    '{name} jieqaf jaħdem, u t-tħaddim li qed jistenna biex jibda jiġi kkanċellat. It-tħaddim ta’ qabel u ċ-chats tiegħu jinżammu.',
  archiveConfirm: 'Arkivja {name}',
  deleteMemoryTitle: 'Tħassar il-memorja?',
  deleteMemoryDescription: 'Poneva mhux se tibqa’ tuża “{name}” fiċ-chats tiegħek.',
  sourceEditReason: 'Itlob lis-sid ta’ dan is-sors biex ibiddlu.',
  agentStatusOff: 'Mitfi',
  agentStatusOn: 'Mixgħul',
  agentStatusOnDemand: 'Fuq talba',
  agentStatusNeedsYou: 'Qed jistennik',
  agentTurnOn: 'Ixgħel',
  agentSwitchedOn: 'Mixgħul.',
  agentStartsWhenOn: '{schedule} · jibda meta jinxtegħel',
  agentTurnOnQuestion: 'Jaħdem {schedule} ({place}). Ixgħel?',
  agentTurnOnCustom: 'Jaħdem fuq skeda personalizzata ({place}). Ixgħel?',
  scheduleHourly: 'Kull siegħa',
  scheduleDaily: 'Kuljum fil-{time}',
  scheduleWeekdays: 'Fil-ġranet tax-xogħol fil-{time}',
  scheduleWeekly: 'Kull {weekday} fil-{time}',
  scheduleCustom: 'Skeda personalizzata',
  scheduleNone: 'Mhux skedat',
  searchAgents: 'Fittex aġenti',
};

const nl: Copy = {
  moreActions: 'Meer handelingen',
  moreActionsFor: 'Meer handelingen voor {name}',
  notifications: 'Meldingen',
  dismiss: 'Sluiten',
  undo: 'Ongedaan maken',
  tryAgain: 'Opnieuw proberen',
  runNow: 'Nu uitvoeren',
  runNowDetail: 'Voert de gepubliceerde versie één keer uit.',
  runFailedNoResult: 'Uitvoering mislukt · Er is geen resultaat opgeslagen.',
  publishFirst: 'Publiceer eerst een versie.',
  setUpConnections: 'Koppelingen instellen',
  invitationRevoked: 'Uitnodiging voor {email} ingetrokken.',
  waitingCount: 'Wachtend: {count}',
  passagesInSearch: 'Passages in zoeken: {count}',
  runFailed: '{name} kon niet worden uitgevoerd.',
  switchOffFailed: '{name} kon niet worden uitgeschakeld.',
  archiveFailed: '{name} kon niet worden gearchiveerd.',
  archiveTitle: 'Agent archiveren?',
  archiveDescription:
    '{name} stopt met uitvoeren, en uitvoeringen die wachten om te starten worden geannuleerd. Eerdere uitvoeringen en hun chats blijven bewaard.',
  archiveConfirm: '{name} archiveren',
  deleteMemoryTitle: 'Herinnering verwijderen?',
  deleteMemoryDescription: 'Poneva gebruikt ‘{name}’ niet meer in je chats.',
  sourceEditReason: 'Vraag de eigenaar van deze bron om dit te wijzigen.',
  agentStatusOff: 'Uit',
  agentStatusOn: 'Aan',
  agentStatusOnDemand: 'Op aanvraag',
  agentStatusNeedsYou: 'Wacht op jou',
  agentTurnOn: 'Aanzetten',
  agentSwitchedOn: 'Aangezet.',
  agentStartsWhenOn: '{schedule} · start zodra hij aan staat',
  agentTurnOnQuestion: 'Draait {schedule} ({place}). Aanzetten?',
  agentTurnOnCustom: 'Draait volgens een aangepast schema ({place}). Aanzetten?',
  scheduleHourly: 'Elk uur',
  scheduleDaily: 'Elke dag om {time}',
  scheduleWeekdays: 'Doordeweeks om {time}',
  scheduleWeekly: 'Elke {weekday} om {time}',
  scheduleCustom: 'Aangepast schema',
  scheduleNone: 'Niet gepland',
  searchAgents: 'Agents zoeken',
};

const pl: Copy = {
  moreActions: 'Więcej akcji',
  moreActionsFor: 'Więcej akcji dla {name}',
  notifications: 'Powiadomienia',
  dismiss: 'Zamknij',
  undo: 'Cofnij',
  tryAgain: 'Spróbuj ponownie',
  runNow: 'Uruchom teraz',
  runNowDetail: 'Uruchamia opublikowaną wersję jeden raz.',
  runFailedNoResult: 'Uruchomienie nie powiodło się · Nie zapisano żadnego wyniku.',
  publishFirst: 'Najpierw opublikuj wersję.',
  setUpConnections: 'Skonfiguruj połączenia',
  invitationRevoked: 'Zaproszenie dla {email} zostało wycofane.',
  waitingCount: 'Oczekuje: {count}',
  passagesInSearch: 'Fragmenty w wyszukiwaniu: {count}',
  runFailed: 'Nie udało się uruchomić: {name}.',
  switchOffFailed: 'Nie udało się wyłączyć: {name}.',
  archiveFailed: 'Nie udało się zarchiwizować: {name}.',
  archiveTitle: 'Zarchiwizować agenta?',
  archiveDescription:
    '{name} przestanie działać, a uruchomienia czekające na start zostaną anulowane. Wcześniejsze uruchomienia i ich czaty zostaną zachowane.',
  archiveConfirm: 'Zarchiwizuj {name}',
  deleteMemoryTitle: 'Usunąć wspomnienie?',
  deleteMemoryDescription: 'Poneva przestanie używać „{name}” w twoich czatach.',
  sourceEditReason: 'Poproś właściciela tego źródła o zmianę.',
  agentStatusOff: 'Wyłączony',
  agentStatusOn: 'Włączony',
  agentStatusOnDemand: 'Na żądanie',
  agentStatusNeedsYou: 'Czeka na Ciebie',
  agentTurnOn: 'Włącz',
  agentSwitchedOn: 'Włączono.',
  agentStartsWhenOn: '{schedule} · uruchomi się po włączeniu',
  agentTurnOnQuestion: 'Uruchamia się {schedule} ({place}). Włączyć?',
  agentTurnOnCustom: 'Uruchamia się według niestandardowego harmonogramu ({place}). Włączyć?',
  scheduleHourly: 'Co godzinę',
  scheduleDaily: 'Codziennie o {time}',
  scheduleWeekdays: 'W dni robocze o {time}',
  scheduleWeekly: 'Co tydzień, {weekday}, o {time}',
  scheduleCustom: 'Niestandardowy harmonogram',
  scheduleNone: 'Bez harmonogramu',
  searchAgents: 'Szukaj agentów',
};

const pt: Copy = {
  moreActions: 'Mais ações',
  moreActionsFor: 'Mais ações para {name}',
  notifications: 'Notificações',
  dismiss: 'Fechar',
  undo: 'Anular',
  tryAgain: 'Tentar novamente',
  runNow: 'Executar agora',
  runNowDetail: 'Executa a versão publicada uma vez.',
  runFailedNoResult: 'A execução falhou · Nenhum resultado foi guardado.',
  publishFirst: 'Publique primeiro uma versão.',
  setUpConnections: 'Configurar ligações',
  invitationRevoked: 'Convite para {email} revogado.',
  waitingCount: 'Pendentes: {count}',
  passagesInSearch: 'Excertos na pesquisa: {count}',
  runFailed: 'Não foi possível executar {name}.',
  switchOffFailed: 'Não foi possível desativar {name}.',
  archiveFailed: 'Não foi possível arquivar {name}.',
  archiveTitle: 'Arquivar o agente?',
  archiveDescription:
    '{name} deixa de ser executado e as execuções que aguardam início são canceladas. As execuções anteriores e os respetivos chats são mantidos.',
  archiveConfirm: 'Arquivar {name}',
  deleteMemoryTitle: 'Apagar a memória?',
  deleteMemoryDescription: 'O Poneva deixará de usar «{name}» nos seus chats.',
  sourceEditReason: 'Peça ao proprietário desta fonte para a alterar.',
  agentStatusOff: 'Desligado',
  agentStatusOn: 'Ligado',
  agentStatusOnDemand: 'A pedido',
  agentStatusNeedsYou: 'Precisa de si',
  agentTurnOn: 'Ligar',
  agentSwitchedOn: 'Ligado.',
  agentStartsWhenOn: '{schedule} · começa quando ligado',
  agentTurnOnQuestion: 'É executado {schedule} ({place}). Ligar?',
  agentTurnOnCustom: 'É executado segundo um agendamento personalizado ({place}). Ligar?',
  scheduleHourly: 'De hora a hora',
  scheduleDaily: 'Todos os dias às {time}',
  scheduleWeekdays: 'Nos dias úteis às {time}',
  scheduleWeekly: 'Todas as semanas, {weekday}, às {time}',
  scheduleCustom: 'Agendamento personalizado',
  scheduleNone: 'Sem agendamento',
  searchAgents: 'Pesquisar agentes',
};

const ro: Copy = {
  moreActions: 'Mai multe acțiuni',
  moreActionsFor: 'Mai multe acțiuni pentru {name}',
  notifications: 'Notificări',
  dismiss: 'Închide',
  undo: 'Anulează',
  tryAgain: 'Încearcă din nou',
  runNow: 'Rulează acum',
  runNowDetail: 'Rulează o dată versiunea publicată.',
  runFailedNoResult: 'Rularea a eșuat · Nu a fost salvat niciun rezultat.',
  publishFirst: 'Publică mai întâi o versiune.',
  setUpConnections: 'Configurează conexiunile',
  invitationRevoked: 'Invitația pentru {email} a fost revocată.',
  waitingCount: 'În așteptare: {count}',
  passagesInSearch: 'Pasaje în căutare: {count}',
  runFailed: '{name} nu a putut fi rulat.',
  switchOffFailed: '{name} nu a putut fi dezactivat.',
  archiveFailed: '{name} nu a putut fi arhivat.',
  archiveTitle: 'Arhivezi agentul?',
  archiveDescription:
    '{name} nu mai rulează, iar rulările care așteaptă să înceapă sunt anulate. Rulările anterioare și chaturile lor se păstrează.',
  archiveConfirm: 'Arhivează {name}',
  deleteMemoryTitle: 'Ștergi amintirea?',
  deleteMemoryDescription: 'Poneva nu va mai folosi „{name}” în chaturile tale.',
  sourceEditReason: 'Cere proprietarului acestei surse să o modifice.',
  agentStatusOff: 'Oprit',
  agentStatusOn: 'Pornit',
  agentStatusOnDemand: 'La cerere',
  agentStatusNeedsYou: 'Te așteaptă',
  agentTurnOn: 'Pornește',
  agentSwitchedOn: 'Pornit.',
  agentStartsWhenOn: '{schedule} · pornește când e activat',
  agentTurnOnQuestion: 'Rulează {schedule} ({place}). Pornești?',
  agentTurnOnCustom: 'Rulează după un program personalizat ({place}). Pornești?',
  scheduleHourly: 'În fiecare oră',
  scheduleDaily: 'În fiecare zi la {time}',
  scheduleWeekdays: 'În zilele lucrătoare la {time}',
  scheduleWeekly: 'În fiecare {weekday} la {time}',
  scheduleCustom: 'Program personalizat',
  scheduleNone: 'Neprogramat',
  searchAgents: 'Caută agenți',
};

const sk: Copy = {
  moreActions: 'Ďalšie akcie',
  moreActionsFor: 'Ďalšie akcie pre {name}',
  notifications: 'Upozornenia',
  dismiss: 'Zavrieť',
  undo: 'Vrátiť späť',
  tryAgain: 'Skúsiť znova',
  runNow: 'Spustiť teraz',
  runNowDetail: 'Raz spustí zverejnenú verziu.',
  runFailedNoResult: 'Spustenie zlyhalo · Neuložil sa žiadny výsledok.',
  publishFirst: 'Najprv zverejnite verziu.',
  setUpConnections: 'Nastaviť pripojenia',
  invitationRevoked: 'Pozvánka pre {email} bola zrušená.',
  waitingCount: 'Čaká: {count}',
  passagesInSearch: 'Úryvky vo vyhľadávaní: {count}',
  runFailed: '{name} sa nepodarilo spustiť.',
  switchOffFailed: '{name} sa nepodarilo vypnúť.',
  archiveFailed: '{name} sa nepodarilo archivovať.',
  archiveTitle: 'Archivovať agenta?',
  archiveDescription:
    '{name} prestane bežať a spustenia, ktoré čakajú na štart, sa zrušia. Predchádzajúce spustenia a ich chaty zostanú zachované.',
  archiveConfirm: 'Archivovať {name}',
  deleteMemoryTitle: 'Vymazať spomienku?',
  deleteMemoryDescription: 'Poneva prestane vo vašich chatoch používať „{name}“.',
  sourceEditReason: 'Požiadajte vlastníka tohto zdroja o zmenu.',
  agentStatusOff: 'Vypnuté',
  agentStatusOn: 'Zapnuté',
  agentStatusOnDemand: 'Na požiadanie',
  agentStatusNeedsYou: 'Čaká na vás',
  agentTurnOn: 'Zapnúť',
  agentSwitchedOn: 'Zapnuté.',
  agentStartsWhenOn: '{schedule} · spustí sa po zapnutí',
  agentTurnOnQuestion: 'Spúšťa sa {schedule} ({place}). Zapnúť?',
  agentTurnOnCustom: 'Spúšťa sa podľa vlastného plánu ({place}). Zapnúť?',
  scheduleHourly: 'Každú hodinu',
  scheduleDaily: 'Každý deň o {time}',
  scheduleWeekdays: 'Cez pracovné dni o {time}',
  scheduleWeekly: 'Týždenne, {weekday} o {time}',
  scheduleCustom: 'Vlastný plán',
  scheduleNone: 'Bez plánu',
  searchAgents: 'Hľadať agentov',
};

const sl: Copy = {
  moreActions: 'Več dejanj',
  moreActionsFor: 'Več dejanj za {name}',
  notifications: 'Obvestila',
  dismiss: 'Zapri',
  undo: 'Razveljavi',
  tryAgain: 'Poskusi znova',
  runNow: 'Zaženi zdaj',
  runNowDetail: 'Enkrat zažene objavljeno različico.',
  runFailedNoResult: 'Zagon ni uspel · Noben rezultat ni bil shranjen.',
  publishFirst: 'Najprej objavite različico.',
  setUpConnections: 'Nastavi povezave',
  invitationRevoked: 'Povabilo za {email} je preklicano.',
  waitingCount: 'Čaka: {count}',
  passagesInSearch: 'Odlomki v iskanju: {count}',
  runFailed: '{name} ni bilo mogoče zagnati.',
  switchOffFailed: '{name} ni bilo mogoče izklopiti.',
  archiveFailed: '{name} ni bilo mogoče arhivirati.',
  archiveTitle: 'Želite arhivirati agenta?',
  archiveDescription:
    '{name} se preneha izvajati, zagoni, ki čakajo na začetek, pa se prekličejo. Pretekli zagoni in njihovi klepeti se ohranijo.',
  archiveConfirm: 'Arhiviraj {name}',
  deleteMemoryTitle: 'Želite izbrisati spomin?',
  deleteMemoryDescription: 'Poneva v vaših klepetih ne bo več uporabljala »{name}«.',
  sourceEditReason: 'Prosite lastnika tega vira, naj to spremeni.',
  agentStatusOff: 'Izklopljeno',
  agentStatusOn: 'Vklopljeno',
  agentStatusOnDemand: 'Na zahtevo',
  agentStatusNeedsYou: 'Čaka na vas',
  agentTurnOn: 'Vklopi',
  agentSwitchedOn: 'Vklopljeno.',
  agentStartsWhenOn: '{schedule} · zažene se, ko je vklopljen',
  agentTurnOnQuestion: 'Zažene se {schedule} ({place}). Vklopim?',
  agentTurnOnCustom: 'Zažene se po prilagojenem urniku ({place}). Vklopim?',
  scheduleHourly: 'Vsako uro',
  scheduleDaily: 'Vsak dan ob {time}',
  scheduleWeekdays: 'Ob delavnikih ob {time}',
  scheduleWeekly: 'Tedensko, {weekday} ob {time}',
  scheduleCustom: 'Prilagojen urnik',
  scheduleNone: 'Brez urnika',
  searchAgents: 'Iskanje agentov',
};

const sv: Copy = {
  moreActions: 'Fler åtgärder',
  moreActionsFor: 'Fler åtgärder för {name}',
  notifications: 'Aviseringar',
  dismiss: 'Stäng',
  undo: 'Ångra',
  tryAgain: 'Försök igen',
  runNow: 'Kör nu',
  runNowDetail: 'Kör den publicerade versionen en gång.',
  runFailedNoResult: 'Körningen misslyckades · Inget resultat sparades.',
  publishFirst: 'Publicera en version först.',
  setUpConnections: 'Konfigurera kopplingar',
  invitationRevoked: 'Inbjudan till {email} har återkallats.',
  waitingCount: 'Väntar: {count}',
  passagesInSearch: 'Avsnitt i sökningen: {count}',
  runFailed: 'Det gick inte att köra {name}.',
  switchOffFailed: 'Det gick inte att stänga av {name}.',
  archiveFailed: 'Det gick inte att arkivera {name}.',
  archiveTitle: 'Arkivera agenten?',
  archiveDescription:
    '{name} slutar köras, och körningar som väntar på att starta avbryts. Tidigare körningar och deras chattar sparas.',
  archiveConfirm: 'Arkivera {name}',
  deleteMemoryTitle: 'Ta bort minnet?',
  deleteMemoryDescription: 'Poneva slutar använda ”{name}” i dina chattar.',
  sourceEditReason: 'Be källans ägare att ändra det här.',
  agentStatusOff: 'Av',
  agentStatusOn: 'På',
  agentStatusOnDemand: 'Vid behov',
  agentStatusNeedsYou: 'Väntar på dig',
  agentTurnOn: 'Slå på',
  agentSwitchedOn: 'Påslagen.',
  agentStartsWhenOn: '{schedule} · startar när den slås på',
  agentTurnOnQuestion: 'Körs {schedule} ({place}). Slå på?',
  agentTurnOnCustom: 'Körs enligt ett anpassat schema ({place}). Slå på?',
  scheduleHourly: 'Varje timme',
  scheduleDaily: 'Varje dag kl. {time}',
  scheduleWeekdays: 'Vardagar kl. {time}',
  scheduleWeekly: 'Varje {weekday} kl. {time}',
  scheduleCustom: 'Anpassat schema',
  scheduleNone: 'Inget schema',
  searchAgents: 'Sök agenter',
};

export const overlaysCopy: NamespaceCatalog<OverlaysCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default overlaysCopy;
